import React, { useState } from 'react';
import { Button, Container, TextField, Typography, Box, CircularProgress } from '@mui/material';
import { Alert } from '@mui/lab';
import axios from 'axios';

/**
 * Resolve the `?next=` parameter into a redirect target.
 *
 * Only same-origin targets are accepted. An open redirect here is the classic
 * phishing chain: real login page, real password, attacker-chosen destination
 * afterwards.
 *
 * Resolve with the SAME parser the browser will use rather than testing the
 * string by hand — a hand-rolled `startsWith('/') && !startsWith('//')` looks
 * airtight and is not: WHATWG treats `\` like `/` for http(s), so `/\evil.com`
 * (URL-encoded `%2F%5Cevil.com`, decoded by URLSearchParams) passes both tests
 * and navigates to `https://evil.com/`. Measured, not assumed.
 *
 * Return the ABSOLUTE href, never `pathname + search + hash`: `/..//evil.com`
 * resolves same-origin but its pathname is `//evil.com`, which as a bare string
 * is protocol-relative again — the same hole one layer down.
 */
function safeNextTarget(): string {
  const next = new URLSearchParams(window.location.search).get('next');
  if (!next) return '/';
  try {
    const url = new URL(next, window.location.origin);
    if (url.origin !== window.location.origin) return '/';
    if (url.pathname === '/login') return '/';
    // `/..//evil.com` resolves same-origin but yields the pathname `//evil.com`.
    // Returning the absolute href already makes that safe — the browser cannot
    // reinterpret an absolute URL as protocol-relative — but it would still dump
    // the operator on a nonsense route. Free to reject; no dashboard path starts '//'.
    if (url.pathname.startsWith('//')) return '/';
    return url.href;
  } catch {
    return '/';
  }
}

export const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        '/api/auth/login',
        { username, password },
        { withCredentials: true }, // Allow cookies
      );

      if (response.data.success) {
        // Store login state
        localStorage.setItem('isLoggedIn', 'true');

        // Optionally store user info
        if (response.data.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.data.user));
        }

        // Back to where the session expired, if the interceptor passed a target.
        window.location.href = safeNextTarget();
      }
    } catch (err: any) {
      setLoading(false);

      if (axios.isAxiosError(err) && err.response) {
        const errorMessage = err.response.data?.error || 'Login fehlgeschlagen';
        const errorDetails = err.response.data?.details || '';
        setError(errorDetails || errorMessage);
      } else {
        setError('Netzwerkfehler. Bitte versuchen Sie es erneut.');
      }
    }
  };

  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      <Box
        component="form"
        onSubmit={submitForm}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2, // space between form elements
          width: '100%',
          maxWidth: 400,
        }}
      >
        <Typography variant="h5" gutterBottom>
          Finanztrends Dashboard
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: 'center',
            mb: 1,
            fontStyle: 'italic',
          }}
        >
          Bitte melden Sie sich mit Ihren WordPress-Zugangsdaten an.
        </Typography>
        <TextField
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          label="Benutzername"
          variant="outlined"
          fullWidth
          required
          disabled={loading}
          autoComplete="username"
        />
        <TextField
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          label="Passwort"
          variant="outlined"
          fullWidth
          required
          disabled={loading}
          autoComplete="current-password"
        />
        <Button type="submit" variant="contained" color="primary" disabled={loading} fullWidth>
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
        </Button>
        {error && <Alert severity="error">{error}</Alert>}
      </Box>
    </Container>
  );
};
